'use client';

import { useServerAction } from 'hooks/use-server-action';
import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { addToCartClientSide } from 'services/cart/actions';
import {
  addEquipment as addEquipmentAction,
  deleteEquipment as deleteEquipmentAction,
  deselectEquipment as deselectEquipmentAction,
  editEquipment as editEquipmentAction,
  selectEquipment as selectEquipmentAction
} from 'services/equipment/actions';
import {
  AddEquipmentDataForm,
  AddEquipmentState,
  DeselectEquipmentResponse,
  EditEquipmentDataForm,
  EditEquipmentState,
  SelectEquipmentResponse
} from 'services/equipment/types';
import {
  AddOrderItemRequest,
  AddOrderItemResponse
} from 'services/order/types';

const CoreContext = createContext<{
  isAddingEquipment: boolean;
  isSelectingEquipment: boolean;
  isDeletingEquipment: boolean;
  isDeselectingEquipment: boolean;
  isEditingEquipment: boolean;
  addEquipment: (
    prevState: AddEquipmentState,
    dataForm: AddEquipmentDataForm & { setAddToastCookie?: boolean }
  ) => Promise<AddEquipmentState>;
  selectEquipment: (
    equipmentId: string,
    serialNumber?: string,
    revalidate?: boolean
  ) => Promise<SelectEquipmentResponse>;
  deleteEquipment: (
    id: string,
    serialNumber?: string
  ) => Promise<DeselectEquipmentResponse>;
  deselectEquipment: () => Promise<DeselectEquipmentResponse>;
  editEquipment: (
    _prevSate: EditEquipmentState,
    formData: EditEquipmentDataForm
  ) => Promise<EditEquipmentState>;
  handleAddToCart: (
    bodyRequest: AddOrderItemRequest,
    storeId: string,
    locale: string
  ) => Promise<{ error: boolean; response?: AddOrderItemResponse }>;
  cartLastUpdated: number;
  setCartLastUpdated: Dispatch<SetStateAction<number>>;
  isAddingToCartPending: boolean;
  broadcastCartUpdate: () => void;
}>(null);

export const useCoreContext = () => useContext(CoreContext);

export const CoreProvider: FC<PropsWithChildren> = ({ children }) => {
  const [addEquipment, isAddingEquipment] = useServerAction(addEquipmentAction);
  const [selectEquipment, isSelectingEquipment] = useServerAction(
    selectEquipmentAction
  );
  const [deleteEquipment, isDeletingEquipment] = useServerAction(
    deleteEquipmentAction
  );
  const [deselectEquipment, isDeselectingEquipment] = useServerAction(
    deselectEquipmentAction
  );
  const [editEquipment, isEditingEquipment] =
    useServerAction(editEquipmentAction);

  const [cartLastUpdated, setCartLastUpdated] = useState<number>();
  const [isAddingToCartPending, setIsAddingToCartPending] =
    useState<boolean>(false);

  const broadcastCartUpdate = useCallback(() => {
    setCartLastUpdated(Date.now());
  }, []);

  const handleAddToCart = useCallback(
    async (
      bodyRequest: AddOrderItemRequest,
      storeId: string,
      locale: string
    ) => {
      let response;
      setIsAddingToCartPending(true);
      try {
        response = await addToCartClientSide(bodyRequest, storeId, locale);

        broadcastCartUpdate();

        return response;
      } catch (error) {
        return { error: true };
      } finally {
        setIsAddingToCartPending(false);
      }
    },
    [broadcastCartUpdate]
  );

  const coreContext = useMemo(
    () => ({
      broadcastCartUpdate,
      cartLastUpdated,
      setCartLastUpdated,
      isAddingToCartPending,
      isAddingEquipment,
      isSelectingEquipment,
      isDeletingEquipment,
      isDeselectingEquipment,
      isEditingEquipment,
      editEquipment,
      selectEquipment,
      addEquipment,
      deleteEquipment,
      handleAddToCart,
      deselectEquipment
    }),
    [
      broadcastCartUpdate,
      cartLastUpdated,
      setCartLastUpdated,
      isAddingToCartPending,
      isAddingEquipment,
      isSelectingEquipment,
      isDeletingEquipment,
      isDeselectingEquipment,
      isEditingEquipment,
      editEquipment,
      selectEquipment,
      addEquipment,
      deleteEquipment,
      handleAddToCart,
      deselectEquipment
    ]
  );
  return (
    <CoreContext.Provider value={coreContext}>{children}</CoreContext.Provider>
  );
};
